import React from "react";
import {
  CheckCircleIcon,
  RefreshIcon,
  ExclamationCircleIcon,
  CurrencyRupeeIcon,
} from "@heroicons/react/outline";
import Badge from "../../../../components/common/Badge";
import Button from "../../../../components/common/Button";

const RefundTab = ({
  order,
  formatCurrency,
  formatDate,
  getRefundColor,
  formatRefundStatus,
  onApprove,
  onProcess,
}) => {
  if (!order.refundDetails?.required) {
    return (
      <div className="text-center py-8">
        <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-3" />
        <p className="text-gray-500">No refund required for this order</p>
      </div>
    );
  }

  const refund = order.refundDetails;

  return (
    <div className="space-y-6">
      {/* Refund Status Card */}
      <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <RefreshIcon className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-orange-900">
                Refund Required
              </h3>
              <p className="text-sm text-orange-700">
                {refund.refundReason || "Exchange value exceeds order total"}
              </p>
            </div>
          </div>
          {/* ✅ Display formatted status */}
          <Badge variant={getRefundColor(refund.refundStatus)} size="lg">
            {formatRefundStatus(refund.refundStatus)}
          </Badge>
        </div>

        <div className="bg-white rounded-lg p-4 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-700 font-medium">Refund Amount</span>
            <span className="text-3xl font-bold text-orange-600">
              {formatCurrency(refund.refundAmount || 0)}
            </span>
          </div>

          {refund.approvalRequired && (
            <div className="flex items-center gap-2 text-sm text-yellow-700 bg-yellow-50 rounded p-2">
              <ExclamationCircleIcon className="w-4 h-4" />
              <span>Manager approval required (amount &gt; ₹10,000)</span>
            </div>
          )}
        </div>
      </div>

      {/* Refund Details */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Refund Details</h3>
        <div className="bg-gray-50 p-4 rounded-lg space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Refund Method</p>
              <p className="font-semibold">
                {refund.refundMethod || "Not specified"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Refund Status</p>
              {/* ✅ Display formatted status */}
              <Badge variant={getRefundColor(refund.refundStatus)}>
                {formatRefundStatus(refund.refundStatus)}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-gray-600">Refund Reason</p>
              <p className="font-medium">
                {refund.refundReason || "Exchange exceeds order value"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Initiated At</p>
              <p className="font-medium">{formatDate(refund.initiatedAt)}</p>
            </div>
          </div>

          {/* Bank Details */}
          {refund.refundMethod === "BANK_TRANSFER" && refund.bankDetails && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Bank Account Details
              </p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-600">Account Name:</span>{" "}
                  <span className="font-medium">
                    {refund.bankDetails.accountName || "-"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Account Number:</span>{" "}
                  <span className="font-medium">
                    {refund.bankDetails.accountNumber || "-"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">IFSC Code:</span>{" "}
                  <span className="font-medium">
                    {refund.bankDetails.ifscCode || "-"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Bank Name:</span>{" "}
                  <span className="font-medium">
                    {refund.bankDetails.bankName || "-"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* UPI Details */}
          {refund.refundMethod === "UPI" && refund.upiDetails && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium text-gray-700 mb-2">
                UPI Details
              </p>
              <div className="text-sm">
                <span className="text-gray-600">UPI ID:</span>{" "}
                <span className="font-medium">
                  {refund.upiDetails.vpa || "-"}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ✅ Action Buttons with new status checks + legacy REFUND_INITIATED support */}
      {(refund.refundStatus === "REFUND_PENDING" ||
        refund.refundStatus === "REFUND_PENDING_APPROVAL" ||
        refund.refundStatus === "REFUND_APPROVED" ||
        refund.refundStatus === "REFUND_INITIATED") && (
        <div className="flex gap-3">
          {refund.refundStatus === "REFUND_PENDING_APPROVAL" ? (
            <Button
              variant="primary"
              onClick={onApprove}
              className="flex-1 flex items-center justify-center gap-2"
            >
              <CheckCircleIcon className="w-5 h-5" />
              Approve Refund
            </Button>
          ) : (
            <Button
              variant="success"
              onClick={onProcess}
              className="flex-1 flex items-center justify-center gap-2"
            >
              <CurrencyRupeeIcon className="w-5 h-5" />
              Process Refund
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default RefundTab;
